/**
 * @spell/gesture-engine
 *
 * Trackpad / mouse gesture registration and matching.
 *
 * Design goals from the brief:
 *  - forgiving about *where* on the pad you drew and *how big* you drew it,
 *  - strict about stroke order and direction of travel,
 *  - aware of curvature, so a circle and a square do not read as the same sigil,
 *  - deterministic, so the server can re-score exactly what the client scored.
 *
 * The pipeline is a hybrid of the classic $1 recogniser (resample + scale +
 * translate normalisation) and a turning-angle profile comparison, with an
 * explicit per-stroke segmentation so multi-stroke sigils keep their order.
 */
import { clamp, type GesturePoint } from '@spell/shared-types';

export const RESAMPLE_COUNT = 64;

export interface NormalizedPoint {
  x: number;
  y: number;
  s: number;
}

export interface GestureTemplateData {
  points: NormalizedPoint[];
  resampleCount: number;
  strokeCount: number;
  turning: number[];
  rawScale: number;
  selfConsistency: number;
}

/* ------------------------------------------------------------------ */
/* Basic geometry                                                      */
/* ------------------------------------------------------------------ */

const dist = (a: { x: number; y: number }, b: { x: number; y: number }): number =>
  Math.hypot(b.x - a.x, b.y - a.y);

function pathLength(pts: { x: number; y: number }[]): number {
  let len = 0;
  for (let i = 1; i < pts.length; i++) len += dist(pts[i - 1], pts[i]);
  return len;
}

/** Split a flat point list into per-stroke runs, preserving order. */
export function splitStrokes(points: GesturePoint[]): GesturePoint[][] {
  const strokes: GesturePoint[][] = [];
  let current: GesturePoint[] = [];
  let currentStroke = points.length ? points[0].s : 0;
  for (const p of points) {
    if (p.s !== currentStroke) {
      if (current.length) strokes.push(current);
      current = [];
      currentStroke = p.s;
    }
    current.push(p);
  }
  if (current.length) strokes.push(current);
  return strokes.filter((s) => s.length >= 2);
}

/** Remove jitter with a short moving average, keeping endpoints intact. */
function smooth(pts: GesturePoint[], window = 3): GesturePoint[] {
  if (pts.length <= window) return pts.slice();
  const half = Math.floor(window / 2);
  const out: GesturePoint[] = [];
  for (let i = 0; i < pts.length; i++) {
    let sx = 0;
    let sy = 0;
    let n = 0;
    for (let j = Math.max(0, i - half); j <= Math.min(pts.length - 1, i + half); j++) {
      sx += pts[j].x;
      sy += pts[j].y;
      n++;
    }
    out.push({ x: sx / n, y: sy / n, t: pts[i].t, s: pts[i].s });
  }
  out[0] = pts[0];
  out[out.length - 1] = pts[pts.length - 1];
  return out;
}

/** Drop consecutive duplicates that would produce NaN directions. */
function dedupe(pts: GesturePoint[], eps = 1e-5): GesturePoint[] {
  const out: GesturePoint[] = [];
  for (const p of pts) {
    const last = out[out.length - 1];
    if (!last || dist(last, p) > eps) out.push(p);
  }
  return out;
}

/**
 * Resample a stroke to exactly `n` evenly spaced points.
 * This is what makes drawing speed irrelevant to the match score.
 */
function resample(pts: GesturePoint[], n: number): GesturePoint[] {
  if (pts.length < 2) return new Array(n).fill(pts[0] ?? { x: 0, y: 0, t: 0, s: 0 });
  const total = pathLength(pts);
  if (total <= 1e-6) return new Array(n).fill(pts[0]);
  const interval = total / (n - 1);
  const out: GesturePoint[] = [pts[0]];
  let acc = 0;
  const src = pts.slice();
  for (let i = 1; i < src.length; i++) {
    const d = dist(src[i - 1], src[i]);
    if (acc + d >= interval) {
      const ratio = (interval - acc) / d;
      const nx = src[i - 1].x + ratio * (src[i].x - src[i - 1].x);
      const ny = src[i - 1].y + ratio * (src[i].y - src[i - 1].y);
      const np: GesturePoint = { x: nx, y: ny, t: src[i].t, s: src[i].s };
      out.push(np);
      src.splice(i, 0, np);
      acc = 0;
    } else {
      acc += d;
    }
  }
  while (out.length < n) out.push(src[src.length - 1]);
  return out.slice(0, n);
}

/* ------------------------------------------------------------------ */
/* Normalisation                                                       */
/* ------------------------------------------------------------------ */

/**
 * Build a comparable representation of a raw gesture.
 *
 * Strokes are resampled proportionally to their length (a long stroke keeps
 * more of the budget), the whole gesture is translated to its centroid and
 * scaled uniformly so the largest dimension is 1. Uniform — not per-axis —
 * scaling matters: it keeps a tall oval distinguishable from a circle.
 */
export function normalizeGesture(raw: GesturePoint[], resampleCount = RESAMPLE_COUNT): GestureTemplateData {
  const strokes = splitStrokes(dedupe(raw)).map((s) => smooth(dedupe(s)));
  if (strokes.length === 0) {
    return {
      points: new Array(resampleCount).fill({ x: 0, y: 0, s: 0 }),
      resampleCount,
      strokeCount: 0,
      turning: new Array(resampleCount - 1).fill(0),
      rawScale: 0,
      selfConsistency: 0,
    };
  }

  const lengths = strokes.map((s) => Math.max(pathLength(s), 1e-4));
  const totalLen = lengths.reduce((a, b) => a + b, 0);

  // Distribute the sample budget by arc length, guaranteeing >= 4 per stroke.
  const minPer = 4;
  const budget = resampleCount - minPer * strokes.length;
  const counts = lengths.map((l) =>
    Math.max(minPer, minPer + Math.round((l / totalLen) * Math.max(budget, 0))),
  );
  // Fix rounding drift so the total is exact.
  let drift = counts.reduce((a, b) => a + b, 0) - resampleCount;
  let idx = 0;
  while (drift !== 0 && idx < counts.length * 8) {
    const i = idx % counts.length;
    if (drift > 0 && counts[i] > minPer) {
      counts[i]--;
      drift--;
    } else if (drift < 0) {
      counts[i]++;
      drift++;
    }
    idx++;
  }

  const merged: NormalizedPoint[] = [];
  strokes.forEach((stroke, i) => {
    const rs = resample(stroke, counts[i]);
    for (const p of rs) merged.push({ x: p.x, y: p.y, s: i });
  });

  // Translate to centroid.
  let cx = 0;
  let cy = 0;
  for (const p of merged) {
    cx += p.x;
    cy += p.y;
  }
  cx /= merged.length;
  cy /= merged.length;

  let maxAbs = 1e-6;
  for (const p of merged) {
    maxAbs = Math.max(maxAbs, Math.abs(p.x - cx), Math.abs(p.y - cy));
  }

  const points = merged.map((p) => ({
    x: (p.x - cx) / maxAbs,
    y: (p.y - cy) / maxAbs,
    s: p.s,
  }));

  return {
    points,
    resampleCount,
    strokeCount: strokes.length,
    turning: turningProfile(points),
    rawScale: totalLen,
    selfConsistency: 1,
  };
}

/** Signed turning angle between consecutive segments — the shape's "curvature". */
export function turningProfile(points: NormalizedPoint[]): number[] {
  const out: number[] = [];
  for (let i = 1; i < points.length; i++) {
    const dx = points[i].x - points[i - 1].x;
    const dy = points[i].y - points[i - 1].y;
    out.push(Math.atan2(dy, dx));
  }
  return out;
}

function angleDelta(a: number, b: number): number {
  let d = a - b;
  while (d > Math.PI) d -= 2 * Math.PI;
  while (d < -Math.PI) d += 2 * Math.PI;
  return d;
}

/* ------------------------------------------------------------------ */
/* Comparison                                                          */
/* ------------------------------------------------------------------ */

export interface GestureScoreBreakdown {
  /** Final 0..1 similarity. */
  score: number;
  /** Point-to-point positional agreement. */
  shape: number;
  /** Direction-of-travel agreement. */
  direction: number;
  /** Curvature / turning agreement. */
  curvature: number;
  /** Stroke-count agreement. */
  strokes: number;
}

const WEIGHTS = { shape: 0.44, direction: 0.28, curvature: 0.2, strokes: 0.08 };

/** Compare a live attempt against one registered template. */
export function compareGesture(
  attempt: GestureTemplateData,
  template: GestureTemplateData,
): GestureScoreBreakdown {
  const n = Math.min(attempt.points.length, template.points.length);
  // A gesture with no strokes is not a bad match, it is *no input* — the
  // degenerate all-zero normalisation would otherwise score on direction and
  // curvature alone and hand out a free Weak cast.
  if (n < 4 || attempt.strokeCount === 0 || template.strokeCount === 0) {
    return { score: 0, shape: 0, direction: 0, curvature: 0, strokes: 0 };
  }

  // --- positional distance ------------------------------------------------
  let sum = 0;
  for (let i = 0; i < n; i++) {
    const a = attempt.points[i];
    const b = template.points[i];
    sum += Math.hypot(a.x - b.x, a.y - b.y);
  }
  const meanDist = sum / n;
  // Normalised space spans roughly [-1, 1]; 0.9 mean distance is unrecognisable.
  const shape = clamp(1 - meanDist / 0.9, 0, 1);

  // --- direction of travel ------------------------------------------------
  const ta = attempt.turning;
  const tb = template.turning;
  const m = Math.min(ta.length, tb.length);
  let dirSum = 0;
  for (let i = 0; i < m; i++) {
    dirSum += Math.abs(angleDelta(ta[i], tb[i]));
  }
  const meanDir = m > 0 ? dirSum / m : Math.PI;
  const direction = clamp(1 - meanDir / (Math.PI * 0.85), 0, 1);

  // --- curvature: compare how the direction *changes*, not its absolute value
  let curvSum = 0;
  let curvN = 0;
  for (let i = 1; i < m; i++) {
    const ca = angleDelta(ta[i], ta[i - 1]);
    const cb = angleDelta(tb[i], tb[i - 1]);
    curvSum += Math.abs(ca - cb);
    curvN++;
  }
  const meanCurv = curvN > 0 ? curvSum / curvN : Math.PI;
  const curvature = clamp(1 - meanCurv / (Math.PI * 0.7), 0, 1);

  // --- stroke count -------------------------------------------------------
  const strokeDiff = Math.abs(attempt.strokeCount - template.strokeCount);
  const strokes = strokeDiff === 0 ? 1 : strokeDiff === 1 ? 0.45 : 0;

  const score = clamp(
    shape * WEIGHTS.shape +
      direction * WEIGHTS.direction +
      curvature * WEIGHTS.curvature +
      strokes * WEIGHTS.strokes,
    0,
    1,
  );

  return { score: r(score), shape: r(shape), direction: r(direction), curvature: r(curvature), strokes };
}

/**
 * Register a gesture from one or two sample strokes.
 *
 * With two samples we keep the one that best represents the pair (lowest
 * average distance to the other) and record how consistent the player was —
 * a sloppy registration widens the accepted band at cast time, so players are
 * not punished twice for their own imprecise template.
 */
export function buildTemplate(samples: GesturePoint[][]): GestureTemplateData {
  const normalized = samples
    .filter((s) => s && s.length >= 4)
    .map((s) => normalizeGesture(s));

  if (normalized.length === 0) {
    return normalizeGesture([]);
  }
  if (normalized.length === 1) {
    return { ...normalized[0], selfConsistency: 0.72 };
  }

  const [a, b] = normalized;
  const agreement = compareGesture(a, b).score;

  // Average the two into a canonical template when they agree; otherwise trust
  // the second (most recent, usually more deliberate) registration.
  let base: GestureTemplateData;
  if (agreement >= 0.55 && a.strokeCount === b.strokeCount) {
    const points = a.points.map((p, i) => ({
      x: (p.x + b.points[i].x) / 2,
      y: (p.y + b.points[i].y) / 2,
      s: p.s,
    }));
    base = {
      points,
      resampleCount: a.resampleCount,
      strokeCount: a.strokeCount,
      turning: turningProfile(points),
      rawScale: (a.rawScale + b.rawScale) / 2,
      selfConsistency: agreement,
    };
  } else {
    base = { ...b, selfConsistency: agreement };
  }
  return base;
}

/**
 * Score a live attempt against a stored template.
 *
 * `selfConsistency` acts as a tolerance dial: a player whose two registrations
 * disagreed gets a slightly generous curve, because their own "correct" gesture
 * is genuinely fuzzy.
 */
export function scoreAttempt(
  attemptPoints: GesturePoint[],
  template: GestureTemplateData,
): GestureScoreBreakdown {
  const attempt = normalizeGesture(attemptPoints, template.resampleCount);
  const raw = compareGesture(attempt, template);
  const tolerance = clamp(1 + (1 - template.selfConsistency) * 0.22, 1, 1.25);
  return { ...raw, score: clamp(r(raw.score * tolerance), 0, 1) };
}

const r = (v: number) => Math.round(v * 1000) / 1000;
